import { estruturaItensMap, estruturaTipos } from '../risrotEstruturaCatalog';
import type { TypeAnomalias } from '../../../../../pages/Reports/Risrot/RegisterRisrot';

export type RisrotInitialSituationSnapshot = Record<string, Record<string, string>>;

export type RisrotDynamicItem = {
	item: string;
	uuid?: string;
};

const RISROT_STRUCTURE_ALIASES: Record<string, string> = {
	'area do justante ao pe da barragem': 'area de justante ao pe da barragem',
	'talude do montante': 'talude de montante',
	'talude do jusante': 'talude de jusante',
	'ondas e assoreamento do pe': 'talude de jusante',
	// eslint-disable-next-line
	'tomada de agua': "tomada d'agua",
	'sistema de drenagem interna': 'drenagem interna / dreno fundo',
};

const RISROT_ITEM_ALIASES: Record<string, string> = {
	'escorregamentos ou abatimentos': 'escorregamentos ou abatimentos',
	'ausencia de protecao': 'ausencia de protecao',
	'fissura/trinca': 'fissura / trinca',
	'fissura / trinca': 'fissura / trinca',
	'deslizamento/rastejo': 'deslizamento / rastejo',
	'areas umidas, encharcadas/fofas ou surgencias': 'areas umidas, encharcadas / fofas ou surgencia',
	'areas umidas, encharcadas / fofas ou surgencias': 'areas umidas, encharcadas / fofas ou surgencia',
	'areas umidas, encharcadas/ fofas ou surgencias': 'areas umidas, encharcadas / fofas ou surgencia',
	'areas umidas, encharcadas/ fofas ou surgencia': 'areas umidas, encharcadas / fofas ou surgencia',
	'presenca de animais (formigueiro, tocas, etc.)': 'presenca de animais (formigueiros, tocas, etc.)',
	'presenca de animais (formigueiros,tocas,etc.)': 'presenca de animais (formigueiros, tocas, etc.)',
	'presenca de animais (formigueiros,tocas,etc)': 'presenca de animais (formigueiros, tocas, etc)',
	'ausencia de boias de sinalizacao': 'ausencia de boias de sinalizacao proximo a tomada',
	'desprendimento de blocos<': 'desprendimento de blocos',
};

type RisrotDetail = TypeAnomalias['detalhes'][0];

function normalizeBaseText(value?: string | null) {
	return (value ?? '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[<>]/g, '')
		.replace(/\s*\/\s*/g, ' / ')
		.replace(/\s*,\s*/g, ', ')
		.replace(/\s+/g, ' ')
		.trim()
		.toLowerCase();
}

export function normalizeRisrotStructureLabel(value?: string | null) {
	const normalizedValue = normalizeBaseText(value);

	return RISROT_STRUCTURE_ALIASES[normalizedValue] ?? normalizedValue;
}

export function normalizeRisrotItemLabel(value?: string | null) {
	const normalizedValue = normalizeBaseText(value);

	return RISROT_ITEM_ALIASES[normalizedValue] ?? normalizedValue;
}

export function getCanonicalRisrotStructureLabel(value?: string | null) {
	const normalizedValue = normalizeRisrotStructureLabel(value);

	return estruturaTipos.find(
		(estrutura) => normalizeBaseText(estrutura) === normalizedValue,
	) ?? value?.trim() ?? '';
}

export function getCanonicalRisrotItemLabel(
	value: string,
	structureName: string,
) {
	const normalizedValue = normalizeRisrotItemLabel(value);
	const fixedItems = estruturaItensMap[getCanonicalRisrotStructureLabel(structureName) as keyof typeof estruturaItensMap];

	return fixedItems?.find(
		(item) => normalizeRisrotItemLabel(item) === normalizedValue,
	) ?? value.trim();
}

export function isSameRisrotStructure(left?: string | null, right?: string | null) {
	return normalizeRisrotStructureLabel(left) === normalizeRisrotStructureLabel(right);
}

export function isSameRisrotItem(left?: string | null, right?: string | null) {
	return normalizeRisrotItemLabel(left) === normalizeRisrotItemLabel(right);
}

export function getRisrotStructureData(
	anomalias: TypeAnomalias[],
	structureName: string,
) {
	return anomalias.find((anomalia) => isSameRisrotStructure(anomalia.estrutura, structureName));
}

export function getRisrotDetailByItem(
	detalhes: RisrotDetail[] | undefined,
	itemName: string,
) {
	return detalhes?.find((detalhe) => isSameRisrotItem(detalhe.item, itemName));
}

export function getRisrotDynamicItems(
	detalhes: RisrotDetail[] | undefined,
	fixedItems: string[],
) {
	const fixedItemsSet = new Set(fixedItems.map((item) => normalizeRisrotItemLabel(item)));

	return (detalhes ?? [])
		.filter((detalhe) => !fixedItemsSet.has(normalizeRisrotItemLabel(detalhe.item)))
		.map((detalhe) => ({
			item: detalhe.item,
			uuid: detalhe.uuid,
		})) as RisrotDynamicItem[];
}

export function createRisrotInitialSituationSnapshot(
	anomalias: TypeAnomalias[],
): RisrotInitialSituationSnapshot {
	return anomalias.reduce<RisrotInitialSituationSnapshot>((snapshot, anomalia) => {
		const canonicalStructure = getCanonicalRisrotStructureLabel(anomalia.estrutura);
		const structureKey = normalizeRisrotStructureLabel(canonicalStructure);
		const structureSnapshot = snapshot[structureKey] ?? {};

		anomalia.detalhes.forEach((detalhe) => {
			structureSnapshot[normalizeRisrotItemLabel(
				getCanonicalRisrotItemLabel(detalhe.item, canonicalStructure),
			)] = detalhe.situacao ?? '';
		});

		snapshot[structureKey] = structureSnapshot;
		return snapshot;
	}, {});
}

export function getRisrotInitialSituation(
	snapshot: RisrotInitialSituationSnapshot,
	structureName: string,
	itemName: string,
) {
	return snapshot[normalizeRisrotStructureLabel(structureName)]?.[normalizeRisrotItemLabel(itemName)] ?? '';
}

function mergeRisrotImages(existingImages: TypeAnomalias['imagens'], nextImages: TypeAnomalias['imagens']) {
	const imageMap = new Map<string, TypeAnomalias['imagens'][number]>();

	[...(existingImages ?? []), ...(nextImages ?? [])].forEach((image, index) => {
		const key = [
			image.data ?? '',
			image.date ?? '',
			image.nome ?? '',
			image.legenda ?? '',
		].join('|') || `risrot-image-${index}`;

		if (!imageMap.has(key)) {
			imageMap.set(key, image);
		}
	});

	return Array.from(imageMap.values());
}

export function mergeRisrotAnomaliasByStructure(
	anomalias: TypeAnomalias[],
) {
	const groupedStructures = new Map<string, TypeAnomalias>();
	const structureOrder: string[] = [];

	anomalias.forEach((anomalia) => {
		const canonicalStructure = getCanonicalRisrotStructureLabel(anomalia.estrutura);
		const structureKey = normalizeRisrotStructureLabel(canonicalStructure);
		const currentStructure = groupedStructures.get(structureKey) ?? {
			...anomalia,
			estrutura: canonicalStructure,
			detalhes: [],
			imagens: [],
		};
		const detailMap = new Map(
			currentStructure.detalhes.map((detalhe) => [
				normalizeRisrotItemLabel(detalhe.item),
				detalhe,
			]),
		);

		anomalia.detalhes.forEach((detalhe) => {
			const canonicalItem = getCanonicalRisrotItemLabel(detalhe.item, canonicalStructure);
			const itemKey = normalizeRisrotItemLabel(canonicalItem);
			const previousDetail = detailMap.get(itemKey) ?? { item: canonicalItem } as RisrotDetail;

			detailMap.set(itemKey, {
				...previousDetail,
				...detalhe,
				item: canonicalItem,
			});
		});

		if (!groupedStructures.has(structureKey)) {
			structureOrder.push(structureKey);
		}

		groupedStructures.set(structureKey, {
			...currentStructure,
			...anomalia,
			estrutura: canonicalStructure,
			detalhes: Array.from(detailMap.values()),
			imagens: mergeRisrotImages(currentStructure.imagens, anomalia.imagens),
		});
	});

	return structureOrder.map((structureKey) => groupedStructures.get(structureKey)!);
}
